/**
 * Design Language's own copies of the page primitives — v3 "daylight editorial".
 * ─────────────────────────────────────────────────────────────────
 * Copied verbatim from src/components/elevation/index.tsx (as of c2583c46d,
 * 2026-10-01): the twelve primitives Design Language pages use. Callout is not
 * copied; no Design Language page uses it.
 *
 * Why a copy (Proposal 09, ~/Vault/_system/proposals/09-elevation-separation.md,
 * § 4 coupling 6 and the move rule in § 7): Elevation's own pages will leave
 * this app, and Design Language stays. It must not borrow Elevation's
 * primitives, so that removing Elevation cannot change or break it. The
 * original stays where it is, untouched, for Elevation's pages. The two will
 * drift on purpose from here on; a change meant for Design Language goes here.
 *
 *   Ground   — warm paper via the .daylight scoped tokens (src/index.css);
 *              under .dark the same --p-* names remap onto the dark ramp,
 *              so primitives carry NO dark: variants.
 *   Cards    — bg-[var(--p-shade)], 1px var(--p-hairline), square corners
 *   Display  — font-serif (Spectral) in ink; gold is spent one phrase at a time
 *   Eyebrows — font-body uppercase tracking-[0.28em] in slate
 *   Data     — font-mono tabular in ink
 *   No shadows, no border-2, no gradients, no rounded corners.
 */

import React from 'react';

// reuse-ack: mirrors src/lib/workbook/utils.ts cn — copied with the primitives so Design Language borrows nothing
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ');
}

// ── Page shell ────────────────────────────────────────────────────

interface PageShellProps {
  children: React.ReactNode;
  className?: string;
}

/** Outermost wrapper for a page.  Sets max-width, padding, font. */
// reuse-ack: mirrors src/components/elevation/index.tsx — Design Language's own copy; Elevation leaves this app (Proposal 09 § 4.6)
export function PageShell({ children, className }: PageShellProps) {
  return (
    <div className={cn('max-w-5xl mx-auto px-6 py-8 font-body text-[var(--p-ink)]', className)}>
      {children}
    </div>
  );
}

// ── Section ───────────────────────────────────────────────────────

interface SectionProps {
  children: React.ReactNode;
  className?: string;
}

// reuse-ack: mirrors src/components/elevation/index.tsx — Design Language's own copy; Elevation leaves this app (Proposal 09 § 4.6)
export function Section({ children, className }: SectionProps) {
  return <div className={cn('mb-10', className)}>{children}</div>;
}

// ── Section label (eyebrow) ───────────────────────────────────────

interface SectionLabelProps {
  children: React.ReactNode;
  className?: string;
}

/** Wide-tracked uppercase eyebrow above a heading — slate, quiet. */
// reuse-ack: mirrors src/components/elevation/index.tsx — Design Language's own copy; Elevation leaves this app (Proposal 09 § 4.6)
export function SectionLabel({ children, className }: SectionLabelProps) {
  return (
    <p className={cn('font-body text-[11px] font-medium uppercase tracking-[0.28em] text-[var(--p-label)] mb-1', className)}>
      {children}
    </p>
  );
}

// ── Heading ───────────────────────────────────────────────────────

interface HeadingProps {
  children: React.ReactNode;
  as?: 'h1' | 'h2' | 'h3' | 'h4';
  size?: 'xl' | 'lg' | 'md' | 'sm';
  className?: string;
}

const headingSize = {
  xl: 'text-4xl tracking-[-0.01em]',
  lg: 'text-2xl tracking-[-0.005em]',
  md: 'text-[19px]',
  sm: 'text-[16px]',
};

/** Display heading — Spectral in ink, normal weight. The voice of the page. */
// reuse-ack: mirrors src/components/elevation/index.tsx — Design Language's own copy; Elevation leaves this app (Proposal 09 § 4.6)
export function Heading({ children, as: Tag = 'h2', size = 'lg', className }: HeadingProps) {
  return (
    <Tag className={cn('font-serif font-normal leading-tight text-[var(--p-ink)]', headingSize[size], className)}>
      {children}
    </Tag>
  );
}

// ── Card ──────────────────────────────────────────────────────────

interface CardProps {
  children: React.ReactNode;
  className?: string;
  /** Set true when the card contains a row list — removes default padding so rows go edge-to-edge */
  flush?: boolean;
  /** Featured plate: carries the 3px gold top bar. Spend it sparingly. */
  featured?: boolean;
}

/** Standard card.  Paper-shade plate, hairline border, square. */
// reuse-ack: mirrors src/components/elevation/index.tsx — Design Language's own copy; Elevation leaves this app (Proposal 09 § 4.6)
export function Card({ children, className, flush, featured }: CardProps) {
  return (
    <div
      className={cn(
        'bg-[var(--p-shade)] border border-[var(--p-hairline)] overflow-hidden',
        featured && 'border-t-[3px] border-t-[var(--p-gold)]',
        !flush && 'p-6',
        className
      )}
    >
      {children}
    </div>
  );
}

// ── Card header (title + optional action, top of a card) ─────────

interface CardHeaderProps {
  label?: string;
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  count?: number | string;
  className?: string;
}

// reuse-ack: mirrors src/components/elevation/index.tsx — Design Language's own copy; Elevation leaves this app (Proposal 09 § 4.6)
export function CardHeader({ label, title, subtitle, action, count, className }: CardHeaderProps) {
  return (
    <div className={cn('px-5 pt-5 pb-4 border-b border-[var(--p-hairline)] flex items-start justify-between gap-4', className)}>
      <div className="min-w-0">
        {label && <SectionLabel>{label}</SectionLabel>}
        <Heading size="md">{title}</Heading>
        {subtitle && (
          <p className="text-sm text-[var(--p-ink-soft)] mt-0.5">{subtitle}</p>
        )}
      </div>
      <div className="flex items-center gap-3 shrink-0">
        {count !== undefined && (
          <Badge variant="outline">{count}</Badge>
        )}
        {action}
      </div>
    </div>
  );
}

// ── Row list ──────────────────────────────────────────────────────

interface RowListProps {
  children: React.ReactNode;
  className?: string;
}

/** Container for a list of <Row> items. Applies hairline dividers. */
// reuse-ack: mirrors src/components/elevation/index.tsx — Design Language's own copy; Elevation leaves this app (Proposal 09 § 4.6)
export function RowList({ children, className }: RowListProps) {
  return (
    <div className={cn('divide-y divide-[var(--p-hairline)]', className)}>
      {children}
    </div>
  );
}

// ── Row ───────────────────────────────────────────────────────────

interface RowProps {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
}

// reuse-ack: mirrors src/components/elevation/index.tsx — Design Language's own copy; Elevation leaves this app (Proposal 09 § 4.6)
export function Row({ children, onClick, className }: RowProps) {
  return (
    <div
      onClick={onClick}
      className={cn(
        'flex items-center gap-3 px-5 py-3 text-sm text-[var(--p-ink-soft)]',
        onClick && 'cursor-pointer hover:bg-[var(--hover-tint)] transition-colors',
        className
      )}
    >
      {children}
    </div>
  );
}

// ── Badge ─────────────────────────────────────────────────────────

interface BadgeProps {
  children: React.ReactNode;
  variant?: 'default' | 'outline' | 'green' | 'amber' | 'red' | 'blue';
  className?: string;
}

/* Accent badges tint with the semantic v3 colors: forest = health,
   gold = priority, clay = urgency, slate = structure. Fills stay quiet
   (12% ink of the accent) so the label carries the meaning. */
const badgeVariants: Record<string, string> = {
  default:  'bg-[var(--hover-lift)] text-[var(--p-ink-soft)]',
  outline:  'border border-[var(--p-hairline-2)] text-[var(--p-ink-soft)] bg-transparent',
  green:    'bg-[color-mix(in_srgb,var(--p-forest)_12%,transparent)] text-[var(--p-forest)] dark:text-[var(--text-ter)]',
  amber:    'bg-[color-mix(in_srgb,var(--p-gold)_12%,transparent)] text-[var(--p-gold)]',
  red:      'bg-[color-mix(in_srgb,var(--p-clay)_12%,transparent)] text-[var(--p-clay)]',
  blue:     'bg-[color-mix(in_srgb,var(--p-label)_12%,transparent)] text-[var(--p-label)]',
};

/** Small inline badge. Uses JetBrains Mono. Square. */
// reuse-ack: mirrors src/components/elevation/index.tsx — Design Language's own copy; Elevation leaves this app (Proposal 09 § 4.6)
export function Badge({ children, variant = 'default', className }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center font-mono text-[10px] uppercase tracking-wider px-2 py-0.5',
        badgeVariants[variant],
        className
      )}
    >
      {children}
    </span>
  );
}

// ── Stat block ────────────────────────────────────────────────────

interface StatProps {
  label: string;
  value: string | number;
  className?: string;
}

/** Label + value. Numbers speak in the instrument voice: mono, tabular. */
// reuse-ack: mirrors src/components/elevation/index.tsx — Design Language's own copy; Elevation leaves this app (Proposal 09 § 4.6)
export function Stat({ label, value, className }: StatProps) {
  return (
    <div className={cn('flex flex-col gap-0.5', className)}>
      <span className="font-body text-[10px] uppercase tracking-[0.22em] text-[var(--p-faint)]">{label}</span>
      <span className="font-mono text-xl text-[var(--p-ink)] [font-variant-numeric:tabular-nums]">{value}</span>
    </div>
  );
}

// ── Empty state ───────────────────────────────────────────────────

interface EmptyStateProps {
  title: string;
  description?: string;
  action?: React.ReactNode;
  className?: string;
}

/** Empty states mean it: one serif line honoring the emptiness, one quiet action. */
// reuse-ack: mirrors src/components/elevation/index.tsx — Design Language's own copy; Elevation leaves this app (Proposal 09 § 4.6)
export function EmptyState({ title, description, action, className }: EmptyStateProps) {
  return (
    <div className={cn('py-12 px-6 text-center', className)}>
      <p className="font-serif text-[17px] text-[var(--p-ink)] mb-1">{title}</p>
      {description && (
        <p className="text-sm text-[var(--p-ink-soft)] mb-4">{description}</p>
      )}
      {action}
    </div>
  );
}

// ── Divider ───────────────────────────────────────────────────────

// reuse-ack: mirrors src/components/elevation/index.tsx — Design Language's own copy; Elevation leaves this app (Proposal 09 § 4.6)
export function Divider({ className }: { className?: string }) {
  return <hr className={cn('border-[var(--p-hairline)] my-6', className)} />;
}
